import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { costCenterService } from "/app/src/services";
import { useTranslation } from "react-i18next";

import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { CostCenter as CostCenterType } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";

/**
 * Renders the CostCenter component which displays information about a specific cost center.
 *
 * This component retrieves cost center data using the costCenterId from URL parameters
 * and displays the details including name, creation date, and description.
 *
 * @returns {JSX.Element} The rendered cost center component.
 */
export default function CostCenter() {
  const { t } = useTranslation();
  const { costCenterId } = useParams();
  const handleNavigationError = useHandleNavigationError();

  // Get the Single Cost Center from URL
  const { data: costCenter } = useQuery({
    queryKey: ["costCenter", costCenterId],
    queryFn: () => {
      return costCenterService.getSingle(costCenterId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { cost_center: {} },
    retry: false,
    select: (data: { cost_center: CostCenterType }) => {
      return data.cost_center;
    },
  });

  const basicInfo = [
    {
      label: t("translation:name"),
      value: costCenter?.name,
    },
    {
      label: t("translation:description"),
      value: costCenter?.description,
    },
    {
      label: t("translation:creationDate"),
      value: (
        <PowerPickTimeZoneToLocal
          date={costCenter?.creationDate}
          format={"MMM DD, YYYY HH:mm"}
        />
      ),
    },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{costCenter?.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Cost Centers", to: "/explorer/cost_centers" },
          { label: costCenter?.name },
        ]}
      />
      <h1 className="text-[36px] font-medium">{costCenter?.name}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Cost Center Id: {costCenter?.id}
      </h2>
      <InfoList header={t("translation:basic_info")} items={basicInfo} />
    </div>
  );
}
